/**
 * features/landing/landingIndexCss.ts
 * ------------------------------------------------------------------
 * Styles for landing variant C (the editorial index). A separate string from
 * landingCss.ts on purpose: this variant shares no visual language with the
 * live page, so mixing them would leave two half-overridden systems in one
 * file. If C is chosen, this replaces landingCss.ts rather than joining it.
 *
 * NEAR-MONOCHROME BY RULE. Ink, paper, one orange accent. Every other colour on
 * the page comes from a product's own screenshot or its single accent dot —
 * which is what stops School Portal's navy, CarSearch's blue and Pasa 7's green
 * from fighting each other, or the page.
 * ------------------------------------------------------------------
 */
export const INDEX_CSS = `
/* PALETTES. The page's rule is one accent and nothing else, so these are the
   only real levers: the paper tone, the ink, and the single accent. Everything
   else on the page is a screenshot, which is exactly why School Portal's navy,
   CarSearch's blue and Pasa 7's green can sit together without clashing. */
.ix {
  --ink:#111317; --ink-2:#3A4049; --muted:#767E8A;
  --paper:#FFFFFF; --wash:#F6F6F4; --line:#E3E3DF;
  --b:#FF6B35;
  --display:Poppins,system-ui,sans-serif; --sans:Inter,system-ui,sans-serif;
  --on-accent:#FFFFFF; --nav-bg:rgba(255,255,255,.92);
  background:var(--paper); color:var(--ink); font-family:var(--sans);
  font-size:16px; line-height:1.6; -webkit-font-smoothing:antialiased;
}
.ix *, .ix *::before, .ix *::after { box-sizing:border-box; }
.ix h1, .ix h2, .ix h3, .ix p, .ix ul, .ix ol { margin:0; }
.ix ul, .ix ol { list-style:none; padding:0; }
.ix a { color:inherit; text-decoration:none; }
.ix button { font:inherit; color:inherit; background:none; border:0; cursor:pointer; padding:0; }
.ix :focus-visible { outline:2px solid var(--b); outline-offset:3px; }
.ix-wrap { max-width:1140px; margin:0 auto; padding-inline:clamp(20px,5vw,40px); }

/* one fade, and only with motion allowed */
.ix-rise { opacity:0; transform:translateY(14px); transition:opacity .6s ease, transform .6s ease; }
.ix-rise.in { opacity:1; transform:none; }
@media (prefers-reduced-motion:reduce) { .ix-rise { opacity:1; transform:none; transition:none; } }

/* ---- Intro curtain: the page splitting in half ----
   Carried over from the main page deliberately — it is the one piece of motion
   this company is recognised by. The panels take the accent, so it belongs to
   whichever palette is showing. */
.ix-intro { position:fixed; inset:0; z-index:100; pointer-events:none; }
.ix-intro-panel { position:absolute; top:0; bottom:0; width:50.5%; background:var(--b);
  transition:transform 1s cubic-bezier(.76,0,.24,1); }
.ix-intro-panel.l { left:0; } .ix-intro-panel.r { right:0; }
.ix--in .ix-intro-panel.l { transform:translateX(-101%); }
.ix--in .ix-intro-panel.r { transform:translateX(101%); }
.ix-intro-mark { position:absolute; inset:0; display:grid; place-items:center; z-index:2; }
.ix--in .ix-intro-mark { opacity:0; transition:opacity .4s ease; }
.ix--in .ix-intro { visibility:hidden; transition:visibility 0s linear 1.1s; }
.ix-intro-inner { opacity:0; transform:translateY(14px);
  animation:ix-introIn .75s cubic-bezier(.2,.7,.2,1) forwards .15s; }
@keyframes ix-introIn { to { opacity:1; transform:none; } }

/* the hero arrives as the panels leave */
.ix-enter { opacity:0; transform:translateY(22px); }
.ix--in .ix-enter { animation:ix-up .85s cubic-bezier(.2,.7,.2,1) forwards; }
.ix--in .e1 { animation-delay:.06s } .ix--in .e2 { animation-delay:.16s }
.ix--in .e3 { animation-delay:.28s } .ix--in .e4 { animation-delay:.40s }
@keyframes ix-up { to { opacity:1; transform:none; } }
@media (prefers-reduced-motion:reduce) {
  .ix-intro { display:none; }
  .ix-enter { opacity:1; transform:none; animation:none; }
}

/* ---- Dark mode ----
   With no palette pinned, the page follows the reader: Signature in light,
   Night in dark. Done in CSS rather than JavaScript so it also follows someone
   who switches their system setting with the page already open. A pinned
   palette (the preview switcher, or ?c=) wins, because .ix.p-* is defined
   after this and matches with the same weight. */
@media (prefers-color-scheme: dark) {
  .ix.auto { --ink:#F2F3F5; --ink-2:#B9BFC8; --muted:#848C97;
    --paper:#0E1013; --wash:#15181D; --line:#272C33; --b:#FF7A49; --on-accent:#0E1013;
    --nav-bg:rgba(14,16,19,.92); }
  /* the window chrome would otherwise glare white against the page */
  .ix.auto .ac-win { background:#1A1E24; border-color:#2B313A; }
  .ix.auto .ac-win-bar { background:#20252C; border-bottom-color:#2B313A; }
  .ix.auto .ac-win-url { background:#161A20; color:#8A93A0; }
}
/* the same treatment when Night is pinned */
.ix.p-night .ac-win { background:#1A1E24; border-color:#2B313A; }
.ix.p-night .ac-win-bar { background:#20252C; border-bottom-color:#2B313A; }
.ix.p-night .ac-win-url { background:#161A20; color:#8A93A0; }

/* ---- Nav ---- */
.ix-nav { position:sticky; top:0; z-index:40; background:var(--nav-bg);
  backdrop-filter:blur(10px); border-bottom:1px solid var(--line); }
.ix-nav-row { display:flex; align-items:center; gap:28px; height:70px; }
.ix-logo { font-family:var(--display); font-weight:600; font-size:1.12rem; letter-spacing:-.02em; }
.ix-logo span { color:var(--b); }
.ix-nav nav { display:flex; gap:24px; margin-left:12px; font-size:.9rem; color:var(--ink-2); }
.ix-nav nav a:hover { color:var(--ink); }
.ix-nav-cta { margin-left:auto; display:flex; align-items:center; gap:14px; }
.ix-nav-cta button { font-size:.9rem; color:var(--ink-2); }
.ix-nav-cta button:hover { color:var(--ink); }
/* Only the section links go on a phone. The sign-in button stays: staff reach
   the hub through it, and hiding it leaves them no way in from a phone. */
@media (max-width:760px) {
  .ix-nav nav { display:none; }
  .ix-nav-row { gap:14px; }
  .ix-nav-cta { gap:10px; }
  .ix-btn { padding:10px 15px; font-size:.78rem; }
}

/* sharp buttons — the one thing carried over, since it is the Acepsle signature */
.ix-btn { display:inline-flex; align-items:center; gap:8px; padding:11px 20px; border-radius:0;
  background:var(--ink); color:var(--paper); font-size:.82rem; font-weight:600; letter-spacing:.04em;
  border:1px solid var(--ink); transition:background .2s ease, color .2s ease, border-color .2s ease; }
.ix-btn:hover { background:var(--b); border-color:var(--b); color:var(--on-accent); }
.ix-btn.lg { padding:15px 26px; font-size:.88rem; }
.ix-btn.line { background:transparent; color:var(--ink); }
.ix-btn.line:hover { background:var(--ink); color:var(--paper); }

/* ---- Hero: type only ---- */
.ix-hero { padding-block:clamp(60px,9vw,130px) clamp(44px,6vw,76px); }
.ix-kicker { font-size:.78rem; font-weight:600; letter-spacing:.14em; text-transform:uppercase;
  color:var(--muted); }
.ix-hero h1 { margin-top:22px; font-family:var(--display); font-weight:600;
  font-size:clamp(2.4rem,6.4vw,4.6rem); line-height:1.02; letter-spacing:-.035em; }
.ix-hero h1 em { font-style:normal; color:var(--muted); }
.ix-lead { margin-top:26px; max-width:62ch; font-size:clamp(1.02rem,1.6vw,1.2rem);
  line-height:1.65; color:var(--ink-2); }
.ix-hero-cta { margin-top:34px; display:flex; flex-wrap:wrap; gap:12px; }

/* ---- Flagship: a line, not a band ---- */
.ix-flag { border-block:1px solid var(--line); background:var(--wash); }
.ix-flag-row { display:flex; flex-wrap:wrap; gap:20px 30px; align-items:center;
  justify-content:space-between; padding-block:clamp(26px,3.4vw,40px); }
.ix-flag-k { font-size:.72rem; font-weight:700; letter-spacing:.18em; text-transform:uppercase;
  color:var(--b); }
.ix-flag h2 { margin-top:10px; font-family:var(--display); font-weight:600;
  font-size:clamp(1.3rem,2.4vw,1.9rem); letter-spacing:-.02em; line-height:1.15; }
.ix-flag h2 em { font-style:normal; color:var(--muted); }
.ix-flag-sum { margin-top:6px; font-size:.95rem; color:var(--ink-2); }

/* ---- The grid, brought back from the previous page ----
   Same geometry as before: 1px lines on a 64px square, faded out with a radial
   mask so it is texture rather than graph paper. Painted only when a style
   class asks for it. */
.ix-bg { position:absolute; inset:0; overflow:hidden; pointer-events:none; }
.ix-hero { position:relative; }
.ix-hero > .ix-wrap { position:relative; z-index:1; }
.ix-grid, .ix-glow { position:absolute; opacity:0; }
.ix-grid { inset:0; background-size:64px 64px;
  -webkit-mask-image:radial-gradient(115% 85% at 22% 4%, #000 32%, transparent 72%);
  mask-image:radial-gradient(115% 85% at 22% 4%, #000 32%, transparent 72%); }
.ix-glow { border-radius:50%; filter:blur(96px); }
.ix-glow.a { width:38rem; height:38rem; top:-15rem; right:-10rem; }
.ix-glow.b { width:28rem; height:28rem; bottom:-14rem; left:-10rem; }

/* ---- 1. Warm: orange in the grid and in the type ---- */
.ix.s-warm { --paper:#FFFBF7; --wash:#FDF3EC; --line:#EFE2D8; }
.ix.s-warm .ix-grid { opacity:1;
  background-image:linear-gradient(rgba(255,107,53,.10) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(255,107,53,.10) 1px, transparent 1px); }
.ix.s-warm .ix-glow.a { opacity:.5; background:rgba(255,107,53,.18); }
.ix.s-warm .ix-hero h1 em { color:var(--b); }
.ix.s-warm .ix-kicker { color:var(--b); }
.ix.s-warm .ix-work h2::after, .ix.s-warm .ix-two h2::after, .ix.s-warm .ix-end h2::after {
  content:""; display:block; width:54px; height:4px; margin-top:16px; background:var(--b); }
.ix.s-warm .ix-kind { color:var(--b); }
.ix.s-warm .ix-dot { box-shadow:0 0 0 3px rgba(255,107,53,.14); }

/* ---- 2. Banded: orange as whole surfaces ---- */
.ix.s-band .ix-grid { opacity:1;
  background-image:linear-gradient(rgba(17,19,23,.05) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(17,19,23,.05) 1px, transparent 1px); }
/* ink lines vanish on a dark ground, so the grid flips with the page */
@media (prefers-color-scheme: dark) {
  .ix.auto.s-band .ix-grid {
    background-image:linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px); }
}
.ix.p-night.s-band .ix-grid {
  background-image:linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px); }
.ix.s-band .ix-flag { background:var(--b); border-color:var(--b); color:#fff; }
.ix.s-band .ix-flag-k { color:rgba(255,255,255,.82); }
.ix.s-band .ix-flag h2 em { color:rgba(255,255,255,.78); }
.ix.s-band .ix-flag-sum { color:rgba(255,255,255,.88); }
.ix.s-band .ix-flag .ix-btn { background:#fff; border-color:#fff; color:var(--b); }
.ix.s-band .ix-flag .ix-btn:hover { background:var(--ink); border-color:var(--ink); color:#fff; }
.ix.s-band .ix-end { background:var(--b); color:#fff; }
.ix.s-band .ix-end .ix-btn { background:#fff; border-color:#fff; color:var(--b); }
.ix.s-band .ix-end .ix-btn.line { background:transparent; color:#fff; border-color:rgba(255,255,255,.55); }
.ix.s-band .ix-end .ix-btn.line:hover { background:#fff; color:var(--b); }
.ix.s-band .ix-foot, .ix.s-band .ix-foot a { color:rgba(255,255,255,.78); border-top-color:rgba(255,255,255,.3); }
.ix.s-band .ix-row:hover { background:rgba(255,107,53,.05); }
.ix.s-band .ix-filters button.on { background:var(--b); border-color:var(--b); color:#fff; }

/* ---- 3. Bold: the old charcoal hero, over the new index ---- */
.ix.s-bold .ix-hero { background:#15171C; color:#fff;
  padding-block:clamp(96px,12vw,150px) clamp(52px,7vw,84px); }
.ix.s-bold .ix-grid { opacity:.55;
  background-image:linear-gradient(rgba(255,255,255,.055) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(255,255,255,.055) 1px, transparent 1px); }
.ix.s-bold .ix-glow.a { opacity:1; background:rgba(255,107,53,.30); }
.ix.s-bold .ix-glow.b { opacity:1; background:rgba(255,107,53,.14); }
.ix.s-bold .ix-hero h1 { color:#fff; }
.ix.s-bold .ix-hero h1 em { color:var(--b); }
.ix.s-bold .ix-kicker { color:rgba(255,255,255,.62); }
.ix.s-bold .ix-lead { color:rgba(255,255,255,.76); }
.ix.s-bold .ix-hero .ix-btn { background:var(--b); border-color:var(--b); color:#fff; }
.ix.s-bold .ix-hero .ix-btn:hover { background:#fff; border-color:#fff; color:#15171C; }
.ix.s-bold .ix-hero .ix-btn.line { background:transparent; color:#fff; border-color:rgba(255,255,255,.45); }
.ix.s-bold .ix-hero .ix-btn.line:hover { background:#fff; color:#15171C; }
.ix.s-bold .ix-nav { background:rgba(21,23,28,.92); border-bottom-color:rgba(255,255,255,.12); }
.ix.s-bold .ix-nav, .ix.s-bold .ix-nav a, .ix.s-bold .ix-nav-cta button { color:#fff; }
.ix.s-bold .ix-nav .ix-btn { background:var(--b); border-color:var(--b); color:#fff; }
.ix.s-bold .ix-flag { background:var(--wash); }
.ix.s-bold .ix-kind, .ix.s-bold .ix-filters button.on { }
.ix.s-bold .ix-filters button.on { background:var(--b); border-color:var(--b); color:#fff; }
.ix.s-bold .ix-end { background:#15171C; color:#fff; }
.ix.s-bold .ix-end h2 { color:#fff; }
.ix.s-bold .ix-end .ix-btn { background:var(--b); border-color:var(--b); color:#fff; }
.ix.s-bold .ix-end .ix-btn.line { background:transparent; color:#fff; border-color:rgba(255,255,255,.45); }
.ix.s-bold .ix-foot, .ix.s-bold .ix-foot a { color:rgba(255,255,255,.6); border-top-color:rgba(255,255,255,.18); }

/* rows get a hover in every style, since the whole index is clickable */
.ix-row { transition:background .18s ease; }
.ix.s-warm .ix-row:hover, .ix.s-bold .ix-row:hover { background:rgba(255,107,53,.045); }

/* ---- The index ---- */
.ix-work { padding-block:clamp(50px,7vw,96px); }
.ix-sec-head { display:flex; flex-wrap:wrap; gap:18px 30px; align-items:baseline;
  justify-content:space-between; }
.ix-work h2, .ix-two h2, .ix-end h2 { font-family:var(--display); font-weight:600;
  letter-spacing:-.03em; line-height:1.05; }
.ix-work h2 { font-size:clamp(1.8rem,3.6vw,2.6rem); }
.ix-filters { display:flex; flex-wrap:wrap; gap:8px; }
.ix-filters button { display:inline-flex; align-items:center; gap:8px; padding:8px 14px;
  border:1px solid var(--line); font-size:.84rem; color:var(--ink-2); }
.ix-filters button span { font-size:.72rem; color:var(--muted); }
.ix-filters button:hover { border-color:var(--ink); color:var(--ink); }
.ix-filters button.on { background:var(--ink); border-color:var(--ink); color:var(--paper); }
.ix-filters button.on span { color:var(--paper); opacity:.6; }

.ix-list { margin-top:clamp(26px,3.4vw,40px); border-top:1px solid var(--line); }
.ix-row { border-bottom:1px solid var(--line); }
.ix-head { display:grid; width:100%; align-items:center; gap:18px; padding:22px 4px;
  grid-template-columns:10px minmax(150px,1.1fr) minmax(0,2fr) 76px 20px; text-align:left; }
.ix-head:hover .ix-name { color:var(--b); }
.ix-dot { width:10px; height:10px; border-radius:50%; }
.ix-name { font-family:var(--display); font-size:1.14rem; font-weight:600; letter-spacing:-.015em;
  transition:color .2s ease; }
.ix-what { color:var(--ink-2); font-size:.93rem; }
.ix-kind { font-size:.68rem; font-weight:700; letter-spacing:.14em; text-transform:uppercase;
  color:var(--muted); text-align:right; }
.ix-sign { color:var(--muted); display:grid; place-items:center; }
.ix-row.open .ix-name { color:var(--b); }
@media (max-width:820px) {
  .ix-head { grid-template-columns:10px minmax(0,1fr) 20px; gap:12px; padding:18px 2px; }
  .ix-what, .ix-kind { display:none; }
}

.ix-detail { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.15fr);
  gap:clamp(22px,4vw,48px); align-items:start; padding:4px 4px clamp(30px,4vw,44px); }
@media (max-width:820px) { .ix-detail { grid-template-columns:minmax(0,1fr); } }
.ix-body { color:var(--ink-2); font-size:.96rem; line-height:1.7; max-width:56ch; }
.ix-chips { margin-top:18px; display:flex; flex-wrap:wrap; gap:7px; }
.ix-chips span { padding:5px 11px; border:1px solid var(--line); font-size:.76rem; color:var(--ink-2); }
.ix-visit { margin-top:20px; display:inline-flex; align-items:center; gap:7px; font-size:.86rem;
  font-weight:600; color:var(--b); border-bottom:1px solid currentColor; padding-bottom:2px; }

/* ---- Services and method ---- */
.ix-two { border-top:1px solid var(--line); background:var(--wash);
  padding-block:clamp(50px,7vw,92px); }
.ix-two-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr));
  gap:clamp(34px,6vw,76px); }
@media (max-width:820px) { .ix-two-grid { grid-template-columns:minmax(0,1fr); } }
.ix-two h2 { font-size:clamp(1.5rem,2.8vw,2rem); }
.ix-plain { margin-top:24px; display:grid; gap:20px; }
.ix-plain b, .ix-steps b { display:block; font-size:1rem; font-weight:600; }
.ix-plain span, .ix-steps span { display:block; margin-top:5px; font-size:.91rem; line-height:1.6;
  color:var(--ink-2); }
.ix-steps { margin-top:24px; display:grid; gap:20px; }
.ix-steps li { padding-left:46px; position:relative; }
.ix-n { position:absolute; left:0; top:2px; font-family:var(--display); font-size:.82rem;
  font-weight:600; color:var(--b); }

/* ---- Close ---- */
.ix-end { padding-block:clamp(56px,8vw,110px); }
.ix-end h2 { font-size:clamp(1.9rem,4.4vw,3.2rem); }
.ix-end-cta { margin-top:30px; display:flex; flex-wrap:wrap; gap:12px; }
.ix-foot { margin-top:clamp(40px,6vw,70px); padding-top:24px; border-top:1px solid var(--line);
  font-size:.82rem; line-height:1.8; color:var(--muted); }
.ix-foot a { border-bottom:1px solid var(--line); }
.ix-foot a:hover { color:var(--ink); }

/* 1 — Signature pinned: the light palette, held even in a dark system */
.ix.p-signature { --ink:#111317; --ink-2:#3A4049; --muted:#767E8A;
  --paper:#FFFFFF; --wash:#F6F6F4; --line:#E3E3DF; --b:#FF6B35; --on-accent:#FFFFFF;
  --nav-bg:rgba(255,255,255,.92); }

/* 2 — Studio: warm paper, deep green accent (nods to the product line) */
.ix.p-studio { --ink:#14171A; --ink-2:#434A52; --muted:#767E8A;
  --paper:#FAF8F3; --wash:#F1EEE6; --line:#E0DBD0; --b:#1F7A4D; --on-accent:#FFFFFF;
  --nav-bg:rgba(250,248,243,.93); }

/* 3 — Night: the page inverted. Screenshots carry all the colour, and they
       sit on dark the way a slide does on a projector. */
.ix.p-night { --ink:#F2F3F5; --ink-2:#B9BFC8; --muted:#848C97;
  --paper:#0E1013; --wash:#15181D; --line:#272C33; --b:#FF6B35; --on-accent:#0E1013;
  --nav-bg:rgba(14,16,19,.92); }
.ix.p-night .ix-chips span, .ix.p-night .ix-filters button { border-color:var(--line); }
.ix.p-night .ix-foot a { border-bottom-color:var(--line); }

/* 4 — Blueprint: cool white, navy ink, a technical blue */
.ix.p-blueprint { --ink:#0C1424; --ink-2:#38435A; --muted:#75809A;
  --paper:#FBFCFE; --wash:#F0F4FA; --line:#DDE5F0; --b:#1D4ED8; --on-accent:#FFFFFF;
  --nav-bg:rgba(251,252,254,.93); }

/* The preview switcher. Only on these preview URLs — it is not part of the
   page, and goes when a palette is chosen. */
.ix-sw { position:fixed; right:18px; bottom:18px; z-index:60; display:flex; gap:6px;
  padding:7px; border-radius:999px; background:var(--paper); border:1px solid var(--line);
  box-shadow:0 10px 30px rgba(0,0,0,.14); }
.ix-sw button { width:30px; height:30px; border-radius:50%; border:2px solid transparent;
  display:grid; place-items:center; }
.ix-sw button span { width:18px; height:18px; border-radius:50%; display:block;
  border:1px solid rgba(128,128,128,.35); }
.ix-sw button.on { border-color:var(--b); }
.ix-sw-label { position:fixed; right:18px; bottom:60px; z-index:60; font-size:.68rem;
  letter-spacing:.14em; text-transform:uppercase; color:var(--muted); background:var(--paper);
  padding:4px 9px; border:1px solid var(--line); }
`;
